/**
 * Where an animal is, and what it is doing, at a given second.
 *
 * Ported from `itinerary` and `sample` in apps/display/public/wildlife.js,
 * and it keeps the property that makes that code good: motion is a PURE
 * FUNCTION OF TIME. Nothing accumulates frame to frame. Give `sample` the
 * same clock and the same route and it returns the same pose, which is why a
 * paused valley resumes exactly where it stopped, why a dropped frame never
 * knocks an animal off its path, and why this file can be tested at all.
 *
 * The arithmetic is copied, including the constants. They were tuned by eye
 * against the paintings on the wall display, and the two screens are meant to
 * show the same valley.
 */

import { ATLAS, IDLES, TRAVEL_ORDER, TURN_ORDER, type Cells, type Species } from './atlas';

export interface Point {
  x: number;
  y: number;
}

export interface Step {
  a: Point;
  b: Point;
  duration: number;
  moving: boolean;
  /** Stationary pivot, played after a rest and before the next journey. */
  turning?: boolean;
  /** Heading on arrival; a resting animal must not flip on stopping. */
  facing?: 1 | -1;
  /** Which rest this is, counting rests only -- seeds the idle choice. */
  restIndex?: number;
  /** Heading of the tree segment, retained through a perch. */
  angle?: number;
  climbDown?: boolean;
  /** Shared arc-distance clock across adjacent journeys with no actual stop. */
  travel?: { start: number; duration: number; from: number; to: number };
}

/** What `arrange` produces for one resident; what `sample` needs. */
export interface Route {
  points: Point[];
  speed: number;
  /** Seconds into the loop this resident starts, so they do not march in step. */
  offset: number;
  /** Stretches rests, so neighbours do not stop and start together. */
  tempo: number;
  /** Hop height, in pixels. */
  height: number;
  seed?: number;
  perchAngle?: number;
  steps?: Step[];
}

export interface Pose {
  x: number;
  y: number;
  /** 1 facing right, -1 facing left. */
  facing: 1 | -1;
  frame: number;
  /** Pixels off the ground: a hop, a bob, a wingbeat. */
  lift: number;
  /** Degrees. */
  tilt: number;
  /** Vertical scale, for breathing. */
  squash: number;
  moving: boolean;
  behavior: string;
  cells: Cells;
  /** Bat lowers into its hanging perch, then rises during the pivot. */
  perch: number;
}

const clamp = (n: number, a = 0, b = 1) => Math.max(a, Math.min(b, n));
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const fraction = (n: number) => n - Math.floor(n);

/** Shared by route playback and the travel-cycle inspector. */
export const hopAir = (beat: number) => clamp((beat - 3 / 12) / (5 / 12));

/** Pose changes describe one action, rather than repeatedly toggling a dive. */
export function idleAlternate(name: Species, action: string, local: number, duration: number): number {
  if (name === 'duck' && action === 'dabble') {
    const progress = clamp(local / duration);
    // Source 0 is already upended; source 1 is afloat. Arrive upright,
    // hold one dive in the rest interior, and recover before turning.
    return progress >= .25 && progress < .65 ? 0 : 1;
  }
  if (action === 'doze' && (name === 'rabbit' || name === 'frog' || name === 'hedgehog' || name === 'squirrel' || name === 'duck')) {
    // These measured first cels restore the raised/upright pose. Fox and
    // raccoon start curled in both cels; flyers need separate landing art.
    const lead = Math.min(.4, duration * .2), recovery = Math.min(.65, duration * .2);
    return local > lead && local < duration - recovery ? 1 : 0;
  }
  const beat = action === 'scratch' || action === 'paw-rub' ? 4 : 1.2;
  return action === 'doze' ? (local > .4 ? 1 : 0)
    : action === 'stretch' ? (local > duration * .65 ? 1 : 0)
    : action === 'blink' ? (local % 3.7 > 3.48 ? 1 : 0)
    : Math.sin(local * beat * Math.PI) > 0 ? 0 : 1;
}

/** Select the registered source drawing, including species-specific corrections. */
export function turnSourceFrame(name: Species, progress: number, seconds: number): number {
  const frame = ATLAS[name].flying ? flyingTurnFrame(name, progress, seconds)
    : Math.min(ATLAS[name].turn.frames - 1, turnFrame(progress));
  return TURN_ORDER[name]?.[frame] ?? frame;
}
const smooth = (n: number) => n * n * (3 - 2 * n);

/** Brief acceleration/braking; the rest of a journey has constant stride speed. */
export function travelProgress(local: number, duration: number): number {
  const t = clamp(local, 0, duration);
  const ramp = Math.min(0.24, duration / 4);
  const distance = t < ramp ? t * t / (2 * ramp)
    : t > duration - ramp ? duration - ramp - (duration - t) ** 2 / (2 * ramp)
    : t - ramp / 2;
  return distance / (duration - ramp);
}

/** Inverse of travelProgress, used to time an uninterrupted leg's waypoints. */
function travelTime(progress: number, duration: number): number {
  const ramp = Math.min(.24, duration / 4), distance = clamp(progress) * (duration - ramp);
  return distance < ramp / 2 ? Math.sqrt(2 * ramp * distance)
    : distance > duration - ramp * 1.5 ? duration - Math.sqrt(2 * ramp * (duration - ramp - distance))
    : distance + ramp / 2;
}

function continuousTravel(steps: Step[]): void {
  for (let start = 0; start < steps.length;) {
    if (!steps[start]!.moving) { start++; continue; }
    let end = start + 1;
    while (end < steps.length && steps[end]!.moving) end++;
    const leg = steps.slice(start, end);
    const duration = leg.reduce((sum, step) => sum + step.duration, 0);
    const distance = leg.reduce((sum, step) => sum + Math.hypot(step.b.x - step.a.x, step.b.y - step.a.y), 0);
    let covered = 0, time = 0;
    if (distance > 0) leg.forEach(step => {
      const from = covered / distance;
      covered += Math.hypot(step.b.x - step.a.x, step.b.y - step.a.y);
      const to = covered / distance, next = travelTime(to, duration);
      step.travel = { start: time, duration, from, to };
      step.duration = next - time;
      time = next;
    });
    start = end;
  }
}

/** A brief head/shoulder lead, quick front passing pose, then replant. */
export function turnFrame(progress: number): number {
  const holds = [0.12, 0.25, 0.39, 0.50, 0.56, 0.70, 0.84, 1];
  const index = holds.findIndex((end) => progress < end);
  return index < 0 ? 7 : index;
}

/** Eight headings, each with four wingbeat poses driven by the flight clock. */
export function flyingTurnFrame(name: Species, progress: number, seconds: number): number {
  const wing = Math.floor(fraction(seconds / ATLAS[name].gait) * 4);
  return wing * 8 + turnFrame(progress);
}

/**
 * Which of the five idles to play at one rest.
 *
 * A seeded shuffle of all five, dealt one per visit, so an animal works
 * through every idle before repeating one and two animals never share a
 * sequence. xorshift32 -- `Math.imul` and the `>>> 0`s are what keep it in
 * unsigned 32-bit the way the original is.
 */
export function idleChoice(visit: number, seed = 1): number {
  const bag = [0, 1, 2, 3, 4];
  let value = ((seed | 0) ^ Math.imul(Math.floor(visit / 5) + 1, 0x9e3779b9)) >>> 0;
  const random = () => {
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    return (value >>> 0) / 4294967296;
  };
  for (let i = 4; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [bag[i], bag[j]] = [bag[j]!, bag[i]!];
  }
  return bag[((visit % 5) + 5) % 5]!;
}

/**
 * The walk there and back, with its rests, as a list of timed steps.
 *
 * Out along the points and back again. A rest at each end, a short pause at
 * every point for a climber. Hoppers have their leg durations rounded to a
 * whole number of hops, so a frog never lands mid-hop at a waypoint.
 */
export function itinerary(name: Species, route: Route): Step[] {
  const rig = ATLAS[name];
  const points = route.points;
  const steps: Step[] = [];
  const visit = [...points.keys()];
  for (let i = points.length - 2; i >= 0; i--) visit.push(i);

  for (let n = 0; n < visit.length - 1; n++) {
    const a = points[visit[n]!]!;
    const b = points[visit[n + 1]!]!;
    const distance = Math.hypot(b.x - a.x, b.y - a.y);
    let duration = distance / (route.speed || rig.speed);
    if (rig.hops) duration = Math.max(1, Math.round(duration / rig.gait)) * rig.gait;
    steps.push({ a, b, duration: Math.max(0.01, duration), moving: true });

    const incoming: 1 | -1 = b.x >= a.x ? 1 : -1;
    const angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI + 90;
    const climbDown = b.y > a.y;
    const next = points[visit[n + 2] ?? visit[1]!]!;
    const outgoing: 1 | -1 = next.x >= b.x ? 1 : -1;
    const end = visit[n + 1] === 0 || visit[n + 1] === points.length - 1;
    const rest = (end ? rig.rest : rig.climbs && !climbDown ? 0.8 : 0) * (route.tempo || 1);
    // On arriving head-first at the bottom, regrip upright BEFORE perching.
    const rightBeforeRest = rig.climbs && climbDown && end;
    if (rightBeforeRest) {
      steps.push({ a: b, b, duration: 1.05, moving: false, turning: true, facing: incoming, angle, climbDown });
    }
    if (rest) {
      steps.push({
        a: b,
        b,
        duration: Math.max(rest, 3.8),
        moving: false,
        facing: incoming,
        restIndex: steps.filter((s) => !s.moving && !s.turning).length,
        angle: rig.climbs && climbDown ? angle - 180 : angle,
        climbDown: rig.climbs ? false : climbDown,
      });
    }
    if (!rightBeforeRest && (end || (rig.climbs ? climbDown !== (next.y > b.y) : incoming !== outgoing))) {
      steps.push({ a: b, b, duration: rig.flying ? 0.76 : 1.05, moving: false, turning: true, facing: incoming, angle, climbDown });
    }
  }
  if (!rig.hops && !rig.flying) continuousTravel(steps);
  return steps;
}

export function sample(name: Species, seconds: number, route: Route): Pose {
  const rig = ATLAS[name];
  const steps = route.steps ?? itinerary(name, route);
  const offset = route.offset || 0;
  const total = steps.reduce((sum, s) => sum + s.duration, 0);
  const cycle = Math.floor((seconds + offset) / total);

  let local = (((seconds + offset) % total) + total) % total;
  let step = steps[steps.length - 1]!;
  for (const s of steps) {
    step = s;
    if (local < s.duration) break;
    local -= s.duration;
  }

  const moving = step.moving;
  const dx = step.b.x - step.a.x;
  const dy = step.b.y - step.a.y;
  const facing: 1 | -1 = step.facing ?? (dx >= 0 ? 1 : -1);
  let progress = moving ? clamp(local / step.duration) : 0;
  let gaitClock = local;
  const travelClock = local + (step.travel?.start ?? 0);
  const travelDuration = step.travel?.duration ?? step.duration;
  // Ease in and out of a stop, rather than stopping dead at a rest.
  if (moving && !rig.hops && !rig.flying) {
    const travelled = travelProgress(travelClock, travelDuration);
    progress = step.travel ? clamp((travelled - step.travel.from) / (step.travel.to - step.travel.from || 1)) : travelled;
    gaitClock = travelled * travelDuration;
  }

  let frame = moving ? Math.floor(fraction(local / rig.gait) * rig.walk.frames) : 0;
  if (moving && !rig.hops && !rig.flying) {
    frame = Math.floor(fraction(gaitClock / rig.gait) * rig.walk.frames);
  }
  let lift = 0;
  let tilt = 0;
  let squash = 1;
  let cells: Cells = rig.walk;
  let behavior = moving ? 'walking' : 'resting';

  if (rig.hops && moving) {
    const hops = Math.round(step.duration / rig.gait);
    const beat = fraction(local / rig.gait);
    // Slots 0–2 are planted take-off preparation; slot 8 starts the painted
    // landing. Keep those feet on the ground instead of floating a crouch.
    const air = hopAir(beat);
    progress = (Math.floor(local / rig.gait) + smooth(air)) / hops;
    lift = air === 0 || air === 1 ? 0 : Math.sin(Math.PI * air) * (route.height || 60) * (name === 'frog' ? 0.52 : 0.4);
    behavior = 'hopping';
    const order = TRAVEL_ORDER[name];
    if (order) frame = order[frame]!;
  } else if (name === 'deer' && !moving && rig.graze) {
    cells = rig.graze;
    frame = Math.floor(local * 2) % cells.frames;
    behavior = 'grazing';
    squash = 1 + Math.sin(local * 3) * 0.006;
  } else if (name === 'fox' || name === 'raccoon') {
    behavior = moving ? 'scurrying' : 'looking';
    lift = moving ? Math.max(0, Math.sin((gaitClock / rig.gait) * Math.PI * 2)) * (route.height || 70) * 0.04 : 0;
  } else if (rig.climbs) {
    behavior = moving ? 'climbing' : 'perching';
    // The painted squirrel points up; turn it about its belly to grip a trunk
    // or branch. A climb is a path along wood, never a walk across grass.
    tilt = moving ? (Math.atan2(dy, dx) * 180) / Math.PI + 90 : step.angle ?? route.perchAngle ?? 0;
  } else if (name === 'duck') {
    behavior = 'swimming';
    lift = Math.sin((seconds + offset) * 2.1) * 1.1;
    frame = Math.floor(fraction((seconds + offset) / rig.gait) * rig.walk.frames);
  }

  if (rig.flying && moving) {
    const order = TRAVEL_ORDER[name]!;
    frame = order[Math.floor(fraction((seconds + offset) / rig.gait) * order.length)]!;
    lift = Math.sin((seconds + offset) * 2.1) * (name === 'bird' ? 2 : 3);
    tilt = Math.sin((seconds + offset) * 1.3) * 2;
    behavior = name === 'bird' || name === 'owl' || name === 'bat' ? 'flying' : 'fluttering';
  }

  if (moving && !rig.hops) {
    const contact = clamp(Math.min(travelClock, travelDuration - travelClock) / 0.18);
    lift *= smooth(contact);
    if (rig.flying) tilt *= smooth(contact);
  }

  if (!moving && !step.turning) {
    const visits = steps.filter((s) => !s.moving && !s.turning).length;
    const choice = idleChoice(
      cycle * visits + (step.restIndex || 0),
      route.seed || Math.round((offset || 1) * 997),
    );
    const action = IDLES[name][choice]!;
    cells = rig.idle;
    // Each idle is two genuinely different painted poses. A slow hold, a
    // small change and breathing, in place of a frozen walk frame.
    const alternate = idleAlternate(name, action, local, step.duration);
    frame = choice * 2 + alternate;
    const envelope = Math.sin(Math.PI * clamp(local / step.duration));
    // Return to a planted neutral transform before the pivot artwork begins.
    // A raw sine at an arbitrary rest length otherwise snaps by up to 2.5°.
    const settle = smooth(clamp(Math.min(local, step.duration - local) / 0.24));
    squash = 1 + Math.sin(local * (action === 'doze' ? 1.6 : 2.6)) * 0.004 * settle;
    tilt = (action === 'stretch' ? Math.sin(local * 1.4) * 2.5
      : action === 'look-around' ? Math.sin(local * 1.2) * 2 : 0) * settle;
    lift = !rig.hops && (action === 'sniff' || action === 'nibble') ? Math.sin(local * 5) * 1.2 * envelope : 0;
    if (rig.hops) squash = 1;
    behavior = name === 'deer' && action === 'graze' ? 'grazing' : action;
    if (name === 'deer' && action === 'graze' && rig.graze) {
      cells = rig.graze;
      frame = Math.floor(local * 2) % cells.frames;
    }
    if (rig.climbs) tilt = step.angle ?? 0;
  }

  if (step.turning) {
    cells = rig.turn;
    const count = cells.frames;
    frame = turnSourceFrame(name, local / step.duration, seconds + offset);
    if (rig.climbs && step.climbDown) frame = count - 1 - frame;
    lift = 0;
    tilt = rig.climbs ? (step.angle ?? 0) - (step.climbDown ? 180 : 0) : 0;
    squash = 1;
    behavior = 'turning';
  }

  // Broad-bodied animals get wider source cells by continuing on another row.
  if (cells.sequenceRows > 1) {
    cells = { ...cells, row: cells.row + Math.floor(frame / cells.columns) };
    frame %= cells.columns;
  }

  return {
    x: mix(step.a.x, step.b.x, progress),
    y: mix(step.a.y, step.b.y, progress),
    facing: rig.climbs ? step.turning ? 1 : (moving ? dy > 0 : step.climbDown) ? -1 : 1 : facing,
    frame,
    lift,
    tilt,
    squash,
    moving,
    behavior,
    cells,
    perch: name === 'bat' && !moving
      ? step.turning ? 1 - smooth(clamp(local / step.duration)) : smooth(clamp(local / 0.3))
      : 0,
  };
}
